import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getErrorMessage } from '../services/api';
import { CheckoutPlan, startCheckout, usePaymentsConfigured } from '../utils/checkout';
import { useToast } from './Toast';
import TrialRequest from './TrialRequest';

// A button that goes to the checkout for a plan (PLAN-6, PLAN-17: the website's checkout page): the Student plan for the
// user, or the Class plan for a class. While online payment is pending (Paddle not configured) it is the free-month
// request instead (PLAN-18, TrialRequest), styled by `trial` where it sits; the button's own label isn't shown then, so
// `trial.offer` can say what the plan gives.
const UpgradeButton: React.FC<{
  plan: CheckoutPlan;
  forClass?: { id: number; name: string };
  className?: string;
  children: React.ReactNode;
  trial?: { offer?: React.ReactNode; as?: 'div' | 'span'; className?: string; noteClassName?: string; buttonClassName?: string; icon?: React.ReactNode };
}> = ({ plan, forClass, className, children, trial }) => {
  const { user } = useAuth();
  const toast = useToast();
  const [opening, setOpening] = useState(false);
  const configured = usePaymentsConfigured();
  if (!user) return null;
  if (configured === false) {
    return <TrialRequest plan={plan} forClass={forClass} buttonClassName={className} {...trial} />;
  }
  const open = async () => {
    setOpening(true);
    try {
      if (await startCheckout(plan, { classId: forClass?.id, className: forClass?.name })) {
        // Leaving for the checkout page: the button stays busy until the browser goes. Coming back with the browser's
        // Back button may restore this page from its cache, still busy: free it then.
        window.addEventListener('pageshow', () => setOpening(false), { once: true });
      } else {
        setOpening(false);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'The payment page could not be opened. Please try again.'));
      setOpening(false);
    }
  };
  return (
    <button type="button" onClick={open} disabled={opening} aria-busy={opening} className={`${className ?? ''} disabled:opacity-70 disabled:cursor-wait`}>
      {children}
    </button>
  );
};

export default UpgradeButton;
